import { Dialog, Transition } from '@headlessui/react'
import React, { Fragment, useEffect, useMemo, useState } from 'react'
import {
    Body,
    ButtonAction,
    ButtonsContainer,
    ContentContainer,
    dialogPanel,
    dialogTitle
} from '../AssumeTreatment/styles'
import { IoCloseSharp } from 'react-icons/io5'
import profile from '../../../assets/svg/profile.svg'
import arrowRight from '../../../assets/svg/rightArrow.svg'
import { UserDataType } from 'types/autentication'
import { getRoleName } from 'utils/getRole'
import { roleToName } from 'components/WithRoleAuthorization/WithRoleAuthorization'
// Componente Spinner simples
const Spinner = ({ size = 'sm' }: { size?: 'sm' | 'md' | 'lg' }) => {
    const sizeClasses = {
        sm: 'w-4 h-4',
        md: 'w-6 h-6', 
        lg: 'w-8 h-8'
    }
    
    return (
        <div className={`${sizeClasses[size]} animate-spin rounded-full border-2 border-white border-t-transparent`} />
    )
}

interface GroupData {
    group_key: string
    group_info: {
        u_notifica_user: { value: string; display_value: string }
        u_number: { value: string; display_value: string }
        u_related_notifications: { value: string; display_value: string }
        u_sector: { value: string; display_value: string }
        u_incident_type: { value: string; display_value: string }
        u_group_damage_rating: { value: string; display_value: string }
        u_location: { value: string; display_value: string }
        u_assigned_to: { value: string; display_value: string }
        sys_id: { value: string; display_value: string }
    }
    total_notifications: number
    notifications: any[]
}

interface GroupAssumeTreatmentModalProps {
    isOpen: boolean
    handleCloseModal: () => void
    handleConfirmAssume: () => void
    currentUser: UserDataType
    groupData: GroupData | null
    isLoading?: boolean
}

const GroupAssumeTreatmentModal: React.FC<GroupAssumeTreatmentModalProps> = ({
    isOpen,
    handleCloseModal,
    handleConfirmAssume,
    currentUser,
    groupData,
    isLoading = false
}) => {
    
    const profileStatus = useMemo(() => {
        if (currentUser?.profile) {
            return getRoleName(currentUser.profile)
        }
    }, [currentUser])

    return (
        <Transition
            appear
            show={isOpen}
            as={Fragment}
            data-testid="test-assume-group-treatment-modal"
        >
            <Dialog
                as="div"
                className="relative z-10"
                onClose={handleCloseModal}
            >
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black bg-opacity-25" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4 text-center">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <Dialog.Panel className={dialogPanel}>
                                <Dialog.Title
                                    as="h3"
                                    className={dialogTitle}
                                >
                                    <p className="text-blueTheme text-xl font-semibold">
                                        Assumir tratamento do grupo de notificações
                                    </p>
                                    <IoCloseSharp
                                        onClick={handleCloseModal}
                                        className="text-blueTheme h-8 w-8 p-1 cursor-pointer"
                                    />
                                </Dialog.Title>
                                <Body>
                                    <p className="text-xs max-w-[600px]">
                                        Esse grupo de notificações está sendo tratado por outra pessoa. 
                                        Ao assumir o tratamento, todas as notificações do grupo ficarão em seu nome.
                                    </p>
                                    
                                    <div className="flex justify-around items-center align-center">
                                        <ContentContainerComponent
                                            title={'Responsável atual'}
                                            profileStatus={roleToName[groupData?.notifications?.[0]?.u_assigned_to?.display_value as keyof typeof roleToName] || 'Perfil não informado'}
                                            name={groupData?.notifications?.[0]?.u_assigned_to?.display_value || 'Não informado'}
                                        />

                                        <img
                                            className="mt-12"
                                            src={arrowRight}
                                            alt="seta"
                                            width={50}
                                        />
                                        <ContentContainerComponent
                                            title={'Novo responsável'}
                                            profileStatus={profileStatus?.title}
                                            name={currentUser?.name}
                                        />
                                    </div>
                                    
                                    <ButtonsContainer>
                                        <ButtonAction
                                            onClick={handleCloseModal}
                                            type="button"
                                            className="text-blueTheme text-sm"
                                        >
                                            Cancelar
                                        </ButtonAction>

                                        <ButtonAction
                                            key={`assume-button-${isLoading}`}
                                            onClick={handleConfirmAssume}
                                            disabled={isLoading}
                                            className={`bg-blueTheme text-white text-sm flex items-center justify-center gap-2 ${
                                                isLoading 
                                                    ? 'opacity-50 cursor-not-allowed' 
                                                    : 'hover:bg-blue-600'
                                            }`}
                                        >
                                            {isLoading && <Spinner size="sm" />}
                                            {isLoading ? 'Assumindo...' : 'Assumir tratamento em grupo'}
                                        </ButtonAction>
                                    </ButtonsContainer>
                                </Body>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    )
}

export default function ContentContainerComponent({
    title,
    profileStatus,
    name
}: {
    title: string
    profileStatus: string
    name: string
}) {
    return (
        <ContentContainer>
            <p className="text-sm text-center font-medium">{title}</p>
            <div className="flex items-center justify-center mt-2">
                <img src={profile} alt="profile" width={24} />
                <span className="ml-3 text-roxo text-base font-semibold">
                    {name}
                </span>
            </div>
            <p className="ml-3 text-darkGray text-center">{profileStatus}</p>
        </ContentContainer>
    )
}

export { GroupAssumeTreatmentModal }
